import type { CogsBasisWindow, PnLReport, PnLRow } from '@/types'
import type { MetricKey } from '@/lib/financeGlossary'

/**
 * Pure logic behind the P&L statement, its chart and its period table
 * (F-FE-N164). One ladder, computed once, read by all three — so the bar a
 * line draws, the figure beside it and the column total under it cannot
 * disagree.
 */

export type StatementKey =
  | 'gross_sales'
  | 'sales_tax'
  | 'pos_fees'
  | 'net_sales'
  | 'cogs'
  | 'cogs_purchases'
  | 'gross_profit'
  | 'operating_expenses'
  | 'staff_meals'
  | 'net_profit'
  | 'refunds_issued'
  | 'net_profit_after_refunds'

/** What a line's bar is painted with — the same identities the chart uses. */
export type StatementTone = 'sales' | 'cogs' | 'opex' | 'neutral' | 'loss'

export interface StatementLine {
  key: StatementKey
  label: string
  /** Glossary entry behind the label's tooltip, where one exists. */
  metric?: MetricKey
  /** A deduction takes money off the line above; a total is what is left. */
  kind: 'deduction' | 'total'
  /** Signed the way it moves the statement: deductions are negative. */
  amount: number
  /** Share of net sales, the statement's 100%. Null above the net sales line. */
  pctOfNetSales: number | null
  tone: StatementTone
  /** Bar geometry as a % of the track: where it starts and how wide it is. */
  barLeft: number
  barWidth: number
  /** The last line of the statement — the figure the page exists to show. */
  isResult: boolean
}

export interface Statement {
  lines: StatementLine[]
  /** Where zero sits on the track, in %. 0 unless a total went negative. */
  zeroAt: number
}

type Summary = PnLReport['summary']

const has = (n: number | undefined) => Math.abs(n ?? 0) > 0.005
const round1 = (n: number) => Math.round(n * 10) / 10

/**
 * The statement, top to bottom. Every rung the backend sends is shown in
 * order and the sums close on screen (F-RPT-03); rungs that are zero for the
 * tenant (POS fees, refunds) are left out rather than printed as noise.
 */
export function buildStatement(summary: Summary | undefined): Statement {
  if (!summary) return { lines: [], zeroAt: 0 }

  const gross = summary.total_revenue
  const tax = summary.total_tax
  const fees = summary.pos_fees ?? 0
  const netSales = summary.net_sales ?? gross - tax - fees
  const cogs = summary.cogs ?? 0
  // F-RPT-N41: which source cost of goods came from, per the tenant's basis.
  const basis = summary.cogs_basis ?? 'at_sale'
  const cogsBought = summary.cogs_purchases ?? 0
  const cogsSold = summary.cogs_at_sale ?? cogs - cogsBought
  const grossProfit = summary.gross_profit ?? netSales - cogs
  const opex = summary.total_expenses
  // F-MM-N30: staff meals at cost — already inside net_profit, shown so it adds up.
  const staffMeals = summary.staff_meals_cost ?? 0
  // F-RPT-N49: shown whenever staff ate, even at ₨0 — the footnote says why.
  const staffAte = (summary.staff_meal_orders ?? 0) > 0
  const netProfit = summary.net_profit
  // F-RPT-N50: the refund's tax already came off above with sales tax, so the
  // rung is the refund without it — and the statement still closes on screen.
  const refunds = (summary.refunds_issued ?? 0) - (summary.refunds_tax ?? 0)
  const afterRefunds = summary.net_profit_after_refunds ?? netProfit - refunds

  type Draft = Omit<StatementLine, 'barLeft' | 'barWidth' | 'pctOfNetSales' | 'isResult'> & {
    from: number
    to: number
    showPct: boolean
  }
  const total = (key: StatementKey, label: string, v: number, showPct = true): Draft => ({
    key,
    label,
    metric: key as MetricKey,
    kind: 'total',
    amount: v,
    tone: v < 0 ? 'loss' : 'sales',
    from: Math.min(0, v),
    to: Math.max(0, v),
    showPct,
  })
  const less = (
    key: StatementKey,
    label: string,
    v: number,
    before: number,
    tone: StatementTone,
    metric: MetricKey | undefined,
    showPct = true,
  ): Draft => ({
    key,
    label,
    metric,
    kind: 'deduction',
    amount: -v,
    tone,
    from: Math.min(before, before - v),
    to: Math.max(before, before - v),
    showPct,
  })

  const drafts: Draft[] = [
    total('gross_sales', 'Gross sales', gross, false),
    less('sales_tax', 'Sales tax', tax, gross, 'neutral', 'sales_tax', false),
    ...(has(fees) ? [less('pos_fees', 'POS and fiscal fees', fees, gross - tax, 'neutral', undefined, false)] : []),
    total('net_sales', 'Net sales', netSales),
    ...(basis === 'purchases'
      ? [less('cogs_purchases', 'Cost of goods (purchases)', cogsBought, netSales, 'cogs', 'cogs_purchases')]
      : basis === 'mixed'
        ? [
            less('cogs', 'Cost of goods sold', cogsSold, netSales, 'cogs', 'cogs'),
            less('cogs_purchases', 'Cost of goods (purchases)', cogsBought, netSales - cogsSold, 'cogs', 'cogs_purchases'),
          ]
        : [less('cogs', 'Cost of goods sold', cogs, netSales, 'cogs', 'cogs')]),
    total('gross_profit', 'Gross profit', grossProfit),
    less('operating_expenses', 'Operating expenses', opex, grossProfit, 'opex', 'operating_expenses'),
    ...(has(staffMeals) || staffAte
      ? [less('staff_meals', 'Staff meals (at cost)', staffMeals, grossProfit - opex, 'opex', undefined)]
      : []),
    total('net_profit', 'Net profit', netProfit),
    ...(has(refunds)
      ? [
          less('refunds_issued', 'Refunds (excl. tax)', refunds, netProfit, 'neutral', 'refunds_issued'),
          total('net_profit_after_refunds', 'Net profit after refunds', afterRefunds),
        ]
      : []),
  ]

  const lo = Math.min(0, ...drafts.map(d => d.from))
  const hi = Math.max(0, ...drafts.map(d => d.to))
  const span = hi - lo
  const at = (v: number) => (span > 0 ? ((v - lo) / span) * 100 : 0)

  // A total keeps its sign (a loss is a negative margin); a deduction reads as
  // the size of the bite it takes.
  const pct = (d: Draft) => {
    if (!d.showPct || netSales < 0.005) return null
    return round1(((d.kind === 'total' ? d.amount : Math.abs(d.amount)) / netSales) * 100)
  }

  const lines = drafts.map((d, i): StatementLine => ({
    key: d.key,
    label: d.label,
    metric: d.metric,
    kind: d.kind,
    amount: d.amount,
    tone: d.tone,
    pctOfNetSales: pct(d),
    barLeft: at(d.from),
    barWidth: at(d.to) - at(d.from),
    isResult: i === drafts.length - 1,
  }))

  return { lines, zeroAt: at(0) }
}

export interface PeriodPoint {
  /** The bucket's ISO start — stable key and sort order. */
  period: string
  orders: number
  grossSales: number
  /** Sales tax plus POS / fiscal fees: what comes off gross to reach net sales. */
  taxAndFees: number
  netSales: number
  cogs: number
  opex: number
  /** F-MM-N30: staff meals at cost, already subtracted in netProfit. */
  staffMeals: number
  netProfit: number
  refunds: number
  netAfterRefunds: number
}

export function periodPoint(r: PnLRow): PeriodPoint {
  const fees = r.pos_fees ?? 0
  const netSales = r.net_sales ?? r.revenue - r.tax - fees
  // F-RPT-N50: without the tax already in taxAndFees, so the row closes.
  const refunds = (r.refunds ?? 0) - (r.refunds_tax ?? 0)
  return {
    period: r.period,
    orders: r.orders,
    grossSales: r.revenue,
    taxAndFees: r.tax + fees,
    netSales,
    cogs: r.cogs ?? 0,
    opex: r.expenses,
    staffMeals: r.staff_meals_cost ?? 0,
    netProfit: r.net_profit,
    refunds,
    netAfterRefunds: r.net_profit_after_refunds ?? r.net_profit - refunds,
  }
}

export type PeriodTotals = Omit<PeriodPoint, 'period'>

/** The table's footer: the sum of exactly the rows above it. */
export function periodTotals(points: PeriodPoint[]): PeriodTotals {
  const zero: PeriodTotals = {
    orders: 0,
    grossSales: 0,
    taxAndFees: 0,
    netSales: 0,
    cogs: 0,
    opex: 0,
    staffMeals: 0,
    netProfit: 0,
    refunds: 0,
    netAfterRefunds: 0,
  }
  return points.reduce((t, p) => {
    for (const k of Object.keys(zero) as (keyof PeriodTotals)[]) t[k] += p[k]
    return t
  }, zero)
}

/** "55.4%" — or an em dash when there are no net sales to take a share of. */
export function shareOf(part: number, whole: number): string {
  if (Math.abs(whole) < 0.005) return '—'
  return `${round1((part / whole) * 100).toFixed(1)}%`
}

/** Is there enough revenue for a share of it to mean anything? */
export function hasNetSales(netSales: number): boolean {
  return netSales > 0.005
}

/**
 * A share of net sales written as a sentence (F-FE-N164 follow-up). A period
 * with expenses and no sales — a closed week, a day before opening — divided
 * by zero and printed "A loss of — of net sales".
 */
export function shareSentence(part: number, netSales: number, suffix: string, noSales: string): string {
  if (!hasNetSales(netSales)) return noSales
  return `${shareOf(part, netSales)} ${suffix}`
}

/** Periods that ended in a loss, for the chart's one-line reading. */
export function lossCount(points: PeriodPoint[], afterRefunds: boolean): number {
  return points.filter(p => (afterRefunds ? p.netAfterRefunds : p.netProfit) < -0.005).length
}

/**
 * F-RPT-N41: the sentence shown under the statement when cost of goods is not
 * the plain per-item figure. Null when there is nothing to explain.
 */
export interface StaffMealsFootnote {
  meals: number
  menuValue: number
  /** Menu value with no ingredient cost because the dishes have no recipe. */
  noRecipe: number
  /** Menu value on purchases-basis days: its food was costed when bought. */
  inPurchases: number
}

/**
 * F-RPT-N49 — what sits behind "Staff meals (at cost)". Secret Sky's meals read
 * ₨0 because it has no recipes, which is right (the food was expensed when
 * bought) but looked like free food. The footnote says how many meals, their
 * menu value, and how much of it is not costed and why. Never an estimate.
 * Null when no staff meal was eaten.
 */
export function staffMealsFootnote(summary: Summary | undefined): StaffMealsFootnote | null {
  const meals = summary?.staff_meal_orders ?? 0
  if (!summary || meals <= 0) return null
  return {
    meals,
    menuValue: summary.staff_meal_value ?? 0,
    noRecipe: summary.staff_meal_value_no_recipe ?? 0,
    inPurchases: summary.staff_meal_value_in_purchases ?? 0,
  }
}

export function cogsBasisNote(basis: CogsBasisWindow | undefined, from: string, to: string): string | null {
  if (basis === 'purchases') {
    return from === to
      ? 'Cost of goods is what you bought. Purchases are counted on the day they were bought, so a bulk-buy day reads low — the week or month is the fairer view.'
      : 'Cost of goods is what you spent on purchases in this period.'
  }
  if (basis === 'mixed') {
    return 'The cost basis changed during this period, so cost of goods is shown as two lines: item costs on some days, purchases on the others.'
  }
  return null
}

/**
 * F-RPT-N42 — on the purchases basis, name the categories whose entries are
 * the cost of goods, so an owner can see at once why a food bill filed under
 * "Other" is sitting in operating expenses. Null off that basis.
 */
export function stockCategoriesNote(
  basis: CogsBasisWindow | undefined,
  defs: ReadonlyArray<{ slug: string; label: string; is_active: boolean; is_inventory?: boolean }>,
): string | null {
  if (basis !== 'purchases' && basis !== 'mixed') return null
  const custom = defs
    .filter(d => d.is_active && !!d.is_inventory && d.slug !== 'inventory_purchase')
    .map(d => d.label)
    .sort((a, b) => a.localeCompare(b))
  if (custom.length === 0) {
    return 'Only entries filed as Stock purchase count as cost of goods. To count a category of your own, add it in Settings → Cost of goods.'
  }
  return `Cost of goods counts entries filed under: Stock purchase, ${custom.join(', ')}. Everything else is an operating expense. Change this in Settings → Cost of goods.`
}

/**
 * F-FE-N203: the P&L headline as the sum it is — sales, less cost of goods,
 * less operating expenses (and refunds, when there were any), equals profit.
 * Every figure is the server's own; nothing here is re-derived except the
 * operating line, which folds the staff-meal cost in so the four cards add up
 * (the statement below still shows it as its own line).
 */
export interface BridgeStep {
  key: 'sales' | 'cogs' | 'opex' | 'refunds' | 'profit'
  amount: number
}

export interface Bridge {
  steps: BridgeStep[]
  sales: number
  profit: number
  /** Share of sales for each cost and the profit; empty with no sales. */
  shares: Partial<Record<BridgeStep['key'], number>>
  /** Costs beyond sales on a loss period, as a share of sales. */
  overrunShare: number
}

export function buildBridge(summary: PnLReport['summary']): Bridge {
  const sales = summary.net_sales ?? summary.total_revenue - summary.total_tax - (summary.pos_fees ?? 0)
  const cogs = summary.cogs ?? (summary.gross_profit != null ? sales - summary.gross_profit : 0)
  const opex = summary.total_expenses + (summary.staff_meals_cost ?? 0)
  // F-RPT-N50: net of the tax already out of sales, so the bridge adds up.
  const refunds = (summary.refunds_issued ?? 0) - (summary.refunds_tax ?? 0)
  const hasRefunds = refunds > 0.005
  const profit = hasRefunds ? (summary.net_profit_after_refunds ?? summary.net_profit - refunds) : summary.net_profit

  const steps: BridgeStep[] = [
    { key: 'sales', amount: sales },
    { key: 'cogs', amount: cogs },
    { key: 'opex', amount: opex },
    ...(hasRefunds ? [{ key: 'refunds' as const, amount: refunds }] : []),
    { key: 'profit', amount: profit },
  ]

  const shares: Bridge['shares'] = {}
  let overrunShare = 0
  if (hasNetSales(sales)) {
    for (const s of steps) if (s.key !== 'sales') shares[s.key] = Math.max(0, (s.amount / sales) * 100)
    if (profit < 0) {
      shares.profit = 0
      overrunShare = (-profit / sales) * 100
    }
  }
  return { steps, sales, profit, shares, overrunShare }
}
